import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { getPortalReports } from "@/lib/portal.functions";
import { runServerFn } from "@/lib/server-fn";
import { PortalPageHeader } from "@/components/portal-shell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Package, Truck, Undo2, AlertTriangle, Receipt, ShoppingCart, Clock, TrendingUp, Calendar, History, Search } from "lucide-react";
import { listReports } from "@/lib/reporting/framework/registry";
import { getReportsLastRun, getPortalAccessibleReportKeys } from "@/lib/portal-reports/runtime.functions";

export const Route = createFileRoute("/_authenticated/portal/reports/")({ component: PortalReports });

function fmtMoney(cents: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
}

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function PortalReports() {
  const fetchFn = useServerFn(getPortalReports);
  const lastRunFn = useServerFn(getReportsLastRun);
  const accessFn = useServerFn(getPortalAccessibleReportKeys);
  const { data, isLoading } = useQuery({
    queryKey: ["portal-reports"],
    queryFn: () => runServerFn(() => fetchFn(), "Loading reports"),
  });
  const { data: lastRun } = useQuery({
    queryKey: ["portal-reports-last-run"],
    queryFn: () => runServerFn(() => lastRunFn(), "Loading"),
  });
  const { data: allowedKeys } = useQuery({
    queryKey: ["portal-reports-access"],
    queryFn: () => runServerFn(() => accessFn(), "Loading"),
  });
  const [search, setSearch] = useState("");

  const grouped = useMemo(() => {
    const allowed = allowedKeys ? new Set(allowedKeys) : null;
    const all = listReports().filter((r) => !allowed || allowed.has(r.key));
    const filtered = search
      ? all.filter((r) =>
          r.title.toLowerCase().includes(search.toLowerCase()) ||
          r.description.toLowerCase().includes(search.toLowerCase()) ||
          r.module.toLowerCase().includes(search.toLowerCase()),
        )
      : all;
    const map = new Map<string, typeof filtered>();
    for (const r of filtered) {
      const k = r.module || "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [search, allowedKeys]);

  const totalReports = allowedKeys
    ? listReports().filter((r) => allowedKeys.includes(r.key)).length
    : listReports().length;

  const kpis = [
    { label: "Orders (30d)", value: data?.orders30 ?? 0, icon: ShoppingCart, to: "/portal/orders" as const },
    { label: "Orders (90d)", value: data?.orders90 ?? 0, icon: ShoppingCart, to: "/portal/orders" as const },
    { label: "Shipped (30d)", value: data?.shipped30 ?? 0, icon: Truck, to: "/portal/orders" as const },
    { label: "Inbound ASNs (30d)", value: data?.asns30 ?? 0, icon: Truck, to: "/portal/inbound" as const },
    { label: "RMAs (30d)", value: data?.rmas30 ?? 0, icon: Undo2, to: "/portal/rmas" as const },
    { label: "On-hand units", value: data?.onHand ?? 0, icon: Package, to: "/portal/inventory" as const },
    { label: "Low-stock SKUs", value: data?.lowStockCount ?? 0, icon: AlertTriangle, to: "/portal/inventory" as const },
  ];

  return (
    <>
      <PortalPageHeader
        title="Reports"
        description="Operational summary for your account."
        actions={
          <div className="flex gap-2">
            <Link to="/portal/reports/subscriptions"><Button variant="outline" size="sm"><Calendar className="h-4 w-4 mr-1" /> Subscriptions</Button></Link>
            <Link to="/portal/reports/history"><Button variant="outline" size="sm"><History className="h-4 w-4 mr-1" /> History</Button></Link>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Analytics Library</h2>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search reports…"
                  className="h-8 pl-7 w-56 text-xs"
                />
              </div>
              <span className="text-xs text-muted-foreground tabular-nums">{totalReports} reports</span>
            </div>
          </div>
          {grouped.length === 0 && (
            <div className="text-xs text-muted-foreground py-6 text-center border border-dashed rounded-md">No reports match "{search}".</div>
          )}
          {grouped.map(([module, items]) => (
            <div key={module} className="space-y-2">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-medium">{module}</div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((r) => {
                  const lr = lastRun?.[r.key];
                  return (
                    <Link key={r.key} to="/portal/reports/$reportKey" params={{ reportKey: r.key }} className="block">
                      <Card className="h-full transition hover:border-primary/50 hover:shadow-sm">
                        <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 space-y-0">
                          <CardTitle className="text-sm font-medium leading-tight">{r.title}</CardTitle>
                          {lr && (
                            <Badge
                              variant={lr.status === "succeeded" ? "secondary" : lr.status === "failed" ? "destructive" : "outline"}
                              className="text-[10px] shrink-0"
                            >
                              {fmtRelative(lr.at)}
                            </Badge>
                          )}
                        </CardHeader>
                        <CardContent>
                          <div className="text-xs text-muted-foreground line-clamp-2">{r.description}</div>
                          <div className="mt-2 text-[11px] text-primary">Open report →</div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        <div className="grid gap-3 sm:grid-cols-2">
          <Link to="/portal/sla" className="block">
            <Card className="transition hover:border-primary/50 hover:shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium">Service Performance</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent><div className="text-xs text-muted-foreground">On-time shipping and order accuracy</div></CardContent>
            </Card>
          </Link>
          <Link to="/portal/profitability" className="block">
            <Card className="transition hover:border-primary/50 hover:shadow-sm">
              <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium">Cost &amp; profitability</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent><div className="text-xs text-muted-foreground">Monthly revenue, fulfillment cost, and margin</div></CardContent>
            </Card>
          </Link>
        </div>
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {kpis.map((c) => (
            <Link key={c.label} to={c.to} className="block">
              <Card className="transition hover:border-primary/50 hover:shadow-sm">
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{c.label}</CardTitle>
                  <c.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">
                    {isLoading ? "—" : c.value.toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">Outstanding Invoices</CardTitle>
              <Receipt className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tracking-tight tabular-nums">
                {data ? fmtMoney(data.outstandingCents, data.currency) : "—"}
              </div>
              <Link to="/portal/invoices" className="text-xs text-primary hover:underline">View invoices →</Link>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium text-muted-foreground">Paid (lifetime)</CardTitle>
              <Receipt className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-semibold tracking-tight tabular-nums">
                {data ? fmtMoney(data.paidCents, data.currency) : "—"}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}